import React, { useContext } from "react";
import { SDLCMetadata } from "@sscs/types";
import { NavigationSidebar, TreeView, useSidebarCollapsed, } from "@sscs/ui";
import { REPORT_PAGES_ROUTES, headerHeight, reproID } from "../../constants";
import { filterByLink } from "../../store/issuesSlice";
import { scrollTo } from "../../store/sdlcReportSlice";
import { convertFinalCheckLabel, getCheck } from "../../utils";
import { LinkToReportTab } from "../common";
import { ReportContext } from "../context";
import { useReportDispatch } from "../hooks";
import FileInfo from "./fileInfo/FileInfo";
import styles from "./navigation.module.scss";
import useActiveReportPage from "./pageLink/useActiveReportPage";
const Navigation = ({ summary }) => {
    var _a;
    const dispatch = useReportDispatch();
    const { diffId, selectedCheck } = useContext(ReportContext);
    const [isCollapsed, setIsCollapsed] = useSidebarCollapsed("report");
    const activePage = useActiveReportPage();
    const scanReport = summary.checks_report.report.scans[selectedCheck];
    if (!scanReport)
        return null;
    const { status, scanLevel, isRepro, isDiff } = convertFinalCheckLabel(scanReport.checks);
    const isLevelCustomized = scanReport.inhibitors.customized;
    const reproCheck = getCheck(scanReport.checks, "repro-check");
    const showReproPage = Boolean(diffId) && Boolean(reproCheck);
    const showDiffPage = Boolean(diffId) && reproCheck === undefined;
    const navSize = isCollapsed ? "small" : "large";
    const handleReleaseStatusClick = () => {
        if (isRepro) {
            // When report is repro report, it makes no sense to filter issues
            // Instead, users are shown reproducibility tab
            dispatch(scrollTo({
                id: reproID,
                offset: headerHeight * 2,
            }));
        }
        else if (!isDiff && status) {
            // Filter issues table only if not diff
            dispatch(filterByLink({ status: { operation: "is", values: [status] } }));
        }
    };
    const { counters } = summary;
    const { statistics } = scanReport;
    const page = (route, title, shortTitle, count) => ({
        id: route === "" ? "summary" : route,
        label: (React.createElement(LinkToReportTab, { underline: "none", id: title, to: route }, title)),
        shortLabel: (React.createElement(LinkToReportTab, { underline: "none", id: title, to: route }, shortTitle)),
        count,
    });
    const selected = activePage !== null && activePage !== void 0 ? activePage : "summary";
    const summaryNodes = [page("", "Summary", "Summary", "→")];
    const sections = [
        {
            title: "Bill of materials",
            shortTitle: "xBOM",
            pages: [
                page(REPORT_PAGES_ROUTES.SBOM_ROUTE, "Software", "SBOM", counters.xbom.sbom),
                page(REPORT_PAGES_ROUTES.SAASBOM_ROUTE, "Services", "SaaSBOM", counters.xbom.saasbom),
                page(REPORT_PAGES_ROUTES.MLBOM_ROUTE, "ML models", "ML-BOM", counters.xbom.mlbom),
                page(REPORT_PAGES_ROUTES.CBOM_ROUTE, "Cryptography", "CBOM", counters.xbom.cbom),
            ],
        },
        {
            title: "Triage",
            shortTitle: "Triage",
            pages: [
                page(REPORT_PAGES_ROUTES.ISSUES_ROUTE, "Issues", "Issues", statistics.quality.issues[SDLCMetadata.policy_metrics_t.issues_total_k]),
                page(REPORT_PAGES_ROUTES.MALWARE_ROUTE, "Malware", "Malware", counters.malware.total_threats),
                page(REPORT_PAGES_ROUTES.VULNERABILITIES_ROUTE, "Vulnerabilities", "Vulns", statistics.vulnerabilities[SDLCMetadata.vulnerability_metrics_t.vuln_total_k] -
                    statistics.vulnerabilities[SDLCMetadata.vulnerability_metrics_t.vuln_triaged_k]),
                page(REPORT_PAGES_ROUTES.SECRETS_ROUTE, "Secrets", "Secrets", counters.secrets.exposed),
                page(REPORT_PAGES_ROUTES.LICENSES_ROUTE, "Licenses", "Licenses", counters.total_licenses),
            ],
        },
        {
            title: "Audit",
            shortTitle: "Audit",
            pages: [
                ...(showDiffPage
                    ? [
                        page(REPORT_PAGES_ROUTES.VERSION_DIFF, "Version diff", "Diff", "→"),
                    ]
                    : []),
                ...(showReproPage
                    ? [
                        page(REPORT_PAGES_ROUTES.REPRODUCIBILITY_ROUTE, "Reproducibility", "Repro", (_a = counters.reproducible_build) !== null && _a !== void 0 ? _a : 0),
                    ]
                    : []),
                page(REPORT_PAGES_ROUTES.BEHAVIOR_ROUTE, "Behaviors", "Behaviors", counters.behavior),
                page(REPORT_PAGES_ROUTES.SIGNATURES_ROUTE, "Signatures", "Signatures", counters.signatures),
                page(REPORT_PAGES_ROUTES.NETWORKING_ROUTE, "Networking", "Networking", counters.networking),
                page(REPORT_PAGES_ROUTES.FILE_BROWSER_ROUTE, "Components", "Components", statistics.components),
                page(REPORT_PAGES_ROUTES.POLICIES_ROUTE, "Policies", "Policies", counters.total_policies),
                page(REPORT_PAGES_ROUTES.STATISTICS_ROUTE, "File statistics", "Stats", "→"),
            ],
        },
    ];
    return (React.createElement(NavigationSidebar, { storageKey: "report", title: "SAFE report", isCollapsed: isCollapsed, onCollapse: setIsCollapsed },
        React.createElement(FileInfo, { releaseStatus: status !== null && status !== void 0 ? status : "pass", fileInfo: scanReport.artifact, scanLevel: scanLevel !== null && scanLevel !== void 0 ? scanLevel : 0, isLevelCustomized: isLevelCustomized, isReproducibilityCheck: isRepro, isDiffCheck: isDiff, size: navSize, scanTimestamp: summary.analysis_summary.scan_timestamp, scanDuration: summary.analysis_summary.scan_duration, onClick: handleReleaseStatusClick }),
        React.createElement("nav", { className: styles.navigation },
            React.createElement(TreeView, { "aria-label": "report-navigation", selected: selected, trees: [
                    { nodes: summaryNodes },
                    ...sections.map(({ title, shortTitle, pages }) => ({
                        title,
                        shortTitle,
                        nodes: pages,
                    })),
                ] }))));
};
export default Navigation;
//# sourceMappingURL=Navigation.js.map